import { TabGroup, TabList } from '@headlessui/react';
import {
  BroadcastIcon,
  CertificateIcon,
  ChartPieSliceIcon,
  DesktopIcon,
  DownloadIcon,
  GearIcon,
  ListChecksIcon,
  LockKeyIcon,
  MapPinAreaIcon,
} from '@phosphor-icons/react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { Button, GlobalLoader, Responsive, Text } from '@components';
import { ROUTES } from '@constants';
import { ApiEndpoint, FormType, StatusType, Translation, VariantType } from '@enums';
import { useAppDispatch, useBreadcrumbItems, useFormError, useFormSaveStatus, useFormSubmissionStatus } from '@hooks';
import { addSnack, submitFormRequest, useCdnSummary } from '@store';
import { cn, validateValueEqual } from '@utils';

export const SideNavigation = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { pathname } = useLocation();
  const { id: application_id } = useParams();

  const formType = FormType.Cdn;
  const cdnSummary = useCdnSummary();

  const { t: tSide } = useTranslation(Translation.Common, { keyPrefix: 'configurationSide' });
  const { t: translate } = useTranslation(Translation.Common);
  const { t: tConfiguration } = useTranslation(Translation.Configuration);

  const submitError = useFormError({ formType });
  const isSubmitting = useFormSubmissionStatus({ formType, endpoint: ApiEndpoint.DEPLOY });
  const saveSuccess = useFormSaveStatus(formType);

  const breadcrumbItems = useBreadcrumbItems(
    pathname,
    {
      cdnSource: cdnSummary,
    },
    translate,
  );

  useEffect(() => {
    if (!submitError && !isSubmitting && saveSuccess) {
      dispatch(
        addSnack({
          type: StatusType.Success,
          message: tConfiguration('deploySuccessful', { name: breadcrumbItems?.[1].name ?? '' }),
        }),
      );
    }
  }, [application_id, isSubmitting, saveSuccess, submitError, tConfiguration, formType]);

  const navigation = [
    {
      name: tSide('dashboard'),
      href: ROUTES.configuration.dashboard,
      icon: ChartPieSliceIcon,
      count: '5',
      current: false,
      background: 'bg-slate-500',
    },
    {
      name: tSide('domain'),
      href: ROUTES.configuration.domain,
      icon: DesktopIcon,
      current: true,
      background: 'bg-red-400',
    },
    {
      name: tSide('origins'),
      href: ROUTES.configuration.origins,
      icon: MapPinAreaIcon,
      count: '12',
      current: false,
      background: 'bg-orange-400',
    },
    {
      name: tSide('ssl'),
      href: ROUTES.configuration.ssl,
      icon: CertificateIcon,
      count: '20+',
      current: false,
      background: 'bg-green-500',
    },
    {
      name: tSide('waf'),
      href: ROUTES.configuration.waf.main,
      icon: BroadcastIcon,
      current: false,
      background: 'bg-blue-300',
    },
    {
      name: tSide('security'),
      href: ROUTES.configuration.security,
      icon: LockKeyIcon,
      current: false,
      background: 'bg-sky-400',
    },
    {
      name: tSide('pageRules'),
      href: ROUTES.configuration.pagerules,
      icon: ListChecksIcon,
      current: false,
      background: 'bg-indigo-400',
    },
    {
      name: tSide('cache'),
      href: ROUTES.configuration.cache,
      icon: DownloadIcon,
      current: false,
      background: 'bg-slate-700',
    },
    {
      name: tSide('configuration'),
      href: ROUTES.configuration.appConfiguration,
      icon: GearIcon,
      current: false,
      background: 'bg-slate-300',
    },
  ];

  navigation?.forEach((item) => {
    if (pathname.includes(item.href)) {
      item.current = true;
    } else item.current = false;
  });

  const handleDeploy = () => {
    dispatch(
      submitFormRequest({
        formData: { application_id },
        formType,
        globalOptions: {
          toast: false,
          response: false,
          endpoint: ApiEndpoint.DEPLOY,
        },
      }),
    );
  };

  const handleNavigateButton = (location) => {
    if (validateValueEqual(pathname, location)) {
      navigate(0);
    } else {
      navigate(location);
    }
  };

  return (
    <>
      <nav className="flex flex-col gap-7 h-full" aria-label="Sidebar">
        <div className="flex flex-row lg:flex-col gap-7 justify-between items-end lg:items-start">
          <div className="flex flex-col gap-3">
            <Text $level={3} color="text-black" className="font-medium">
              {tSide('applicationSetUp')}
            </Text>
            <Text $level={5} color="text-gray-400" className=" font-light">
              {tSide('configurationNavigation')}
            </Text>
          </div>
          <div className="lg:w-full">
            <Button
              className="bg-green-400 bg-opacity-80 text-white rounded-lg shadow-lg w-full"
              onClick={handleDeploy}
              disabled={isSubmitting}
            >
              {tSide('deploy')}
            </Button>
          </div>
        </div>
        <Responsive showAbove="lg">
          <div className="flex flex-1 mt-1">
            <ul className="flex-col flex flex-1 space-y-2 py-4 bg-white bg-opacity-90 shadow-card rounded-2xl">
              <Text $level={5} $customizeColor className="text-gray-400 px-5 pt-2 pb-3">
                {tSide('configurationNavigator')}
              </Text>

              {navigation.map((item) => (
                <li key={item.name} className="flex items-center">
                  <Button
                    variant={VariantType.Clean}
                    onClick={() => handleNavigateButton(item.href)}
                    className={cn(
                      'text-xs relative flex-1 py-2 px-3.5 justify-start enabled:shadow-none',
                      item.current
                        ? 'text-slate-700 bg-slate-500 bg-opacity-5 font-medium'
                        : 'hover:bg-slate-50 text-slate-400 hover:text-slate-700 shadow-none font-normal',
                    )}
                  >
                    {item.current && (
                      <div className="absolute w-1 h-5/6 -mx-3.5 bg-theme-primary-main rounded-full"></div>
                    )}
                    <div className="flex gap-x-3 items-center w-full">
                      <div className={`p-2 rounded-lg text-white ${item.background}`}>
                        <item.icon className={'size-4 shrink-0'} />
                      </div>
                      <Text $level={5} $customizeColor>
                        {item.name}
                      </Text>
                    </div>
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        </Responsive>
        <Responsive showBelow="lg">
          <TabGroup>
            <TabList className="flex gap-4 border-b pb-2 max-w-full overflow-x-auto hidden-scrollbar">
              {navigation.map((item) => (
                <li key={item.name} className="flex items-center">
                  <Button
                    onClick={() => handleNavigateButton(item.href)}
                    className={cn(
                      'flex-1 relative gap-x-3 rounded-lg enabled:shadow-none',
                      item.current
                        ? 'text-slate-700 bg-slate-500 bg-opacity-5 font-medium'
                        : 'bg-transparent text-slate-400 hover:text-slate-700',
                    )}
                  >
                    <Text $level={5} $customizeColor className="text-justify">
                      {item.name}
                    </Text>
                  </Button>
                </li>
              ))}
            </TabList>
          </TabGroup>
        </Responsive>
      </nav>
      {isSubmitting && (
        <div className="fixed inset-0 bg-white bg-opacity-20 backdrop-blur-sm flex items-center justify-center z-50">
          <GlobalLoader />
        </div>
      )}
    </>
  );
};
